import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import type { UserRole } from '@brickup/shared/enums'
import { authUserQuery } from '@/api/auth'
import { mayOpen } from '@/constants/navigation-permissions'
import { currentUserRoles } from '@/stores/use-app-store'

/** Thrown when the signed-in role may not see the route it entered. Routes
 *  render it through their `errorComponent`. */
export class AccessDeniedError extends Error {
  constructor(readonly path: string) {
    super(`Access denied: ${path}`)
    this.name = 'AccessDeniedError'
  }
}

/**
 * Route guard for everything behind a login.
 *
 * Resolving the user *before* the route renders is what removes the flash of
 * an empty screen the old app showed while it decided whether to redirect — a
 * route either has a user by the time it mounts or was never entered.
 *
 * `target` carries where the visitor was headed, so the login form can send
 * them on rather than dropping them at a dashboard.
 */
export async function requireAuth(
  queryClient: QueryClient,
  href: string,
): Promise<{ roles: readonly UserRole[] }> {
  try {
    const user = await queryClient.ensureQueryData(authUserQuery)
    // Roles root is trying on decide what the guard admits, so an area they
    // stepped out of turns them away the way it turns those roles away. The
    // server still answers as root; this is the frontend showing itself.
    return { roles: currentUserRoles() ?? user.roles }
  } catch {
    throw redirect({
      to: '/login',
      search: href === '/' ? {} : { target: href },
    })
  }
}

/**
 * The same check the sidebar makes when it decides what to list, applied to
 * the route being entered — so a hidden area is also an unreachable one.
 */
export function requireRoutePermission(
  roles: readonly UserRole[],
  pathname: string,
): void {
  if (!mayOpen(roles, pathname)) {
    throw new AccessDeniedError(pathname)
  }
}
